<template>
  <div style="width: 100%; height: 250px;">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script setup>
  import { ref, onMounted, onBeforeUnmount } from 'vue'
  import { Chart } from 'chart.js'
  import 'chartjs-adapter-moment'
  import moment from 'moment'
  import { eventBus } from '../core/EventBus'

  const props = defineProps({ informacoes: Array })
  const chartCanvas = ref(null)
  let chartInstance = null
  let intervaloRefresh = null
  const dadosHistoricos = ref([])
  let ultimaAtualizacao = 0
  const cores = ['#FF6384', '#36A2EB', '#FFCE56',
    '#4BC0C0', '#9966FF']
  const hiddenPorLabel = {}


  function pluginFundo() {
    return {
      id: 'fundoGradiente',
      beforeDraw(chart) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const isDark = localStorage.getItem('theme') === 'dark';
        const gradColors = isDark ? ['#101720', '#16202D'] : ['#ffffff', '#f0f0f0'];
        const g = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        g.addColorStop(0, gradColors[0]);
        g.addColorStop(1, gradColors[1]);
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = g;
        ctx.fillRect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
        ctx.restore();
      },
    };
  }

  const pluginLinhaVertical = {
    id: 'linhaVertical',
    afterDraw(chart) {
      const tp = chart.tooltip
      if (tp?._active?.length) {
        const ctx = chart.ctx
        const x = tp._active[0].element.x
        const top = chart.scales.y.top
        const bottom = chart.scales.y.bottom
        ctx.save()
        ctx.setLineDash([4, 4])
        ctx.strokeStyle = 'red'
        ctx.beginPath()
        ctx.moveTo(x, top)
        ctx.lineTo(x, bottom)
        ctx.stroke()
        ctx.restore()
      }
    }
  }

  function criarGrafico() {
    if (chartInstance) chartInstance.destroy()
    if (!chartCanvas.value) return

    const isDark = localStorage.getItem('theme') === 'dark';
    const dark = localStorage.getItem('theme') === 'dark'
    const corFonte = dark ? 'white' : 'black'
    const gridColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';

    chartInstance = new Chart(chartCanvas.value, {
      type: 'line',
      data: { datasets: [] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'minute',
              displayFormats: { minute: 'HH:mm' },
              tooltipFormat: 'DD/MM/YYYY HH:mm:ss'
            },
            ticks: { color: corFonte, autoSkip: false },
            grid: {
              color: gridColor,
              lineWidth: 0.5,
              borderDash: []
            }

          },
          y: {
            beginAtZero: false,
            ticks: { color: corFonte },
            grid: {
              color: gridColor,
              lineWidth: 0.5,
              borderDash: []
            }
          }
        },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: { color: corFonte, usePointStyle: true, pointStyle: 'circle' },
            onClick: (e, item, lgd) => {
              const idx = item.datasetIndex
              const meta = lgd.chart.getDatasetMeta(idx)
              meta.hidden = meta.hidden === null
                ? !lgd.chart.data.datasets[idx].hidden
                : null
              hiddenPorLabel[item.text] = meta.hidden ?? false
              lgd.chart.update()
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label(ctx) {
                const med = props.informacoes
                  .find(i => i.descricao === ctx.dataset.label)
                const un = med?.unidadeMedida || ''
                return `${ctx.dataset.label}: ${ctx.parsed.y} ${un}`.trim()
              }
            }
          }
        }
      },
      plugins: [pluginFundo(), pluginLinhaVertical]
    })
  }

  function atualizarGrafico() {
    const agora = moment()
    if (agora.valueOf() - ultimaAtualizacao < 3000) return
    ultimaAtualizacao = agora.valueOf()
    if (!chartInstance) return

    dadosHistoricos.value = dadosHistoricos.value
      .filter(p => moment(p.time).isAfter(agora.clone().subtract(6, 'hours')))

    if (props.informacoes?.length) {
      const registro = { time: agora }
      props.informacoes.forEach(i => (registro[i.descricao] = parseFloat(i.valor)))
      dadosHistoricos.value.push(registro)
    }

    const muitosPontos = dadosHistoricos.value.length > 60

    chartInstance.data.datasets = props.informacoes.map((inf, idx) => ({
      label: inf.descricao,
      data: dadosHistoricos.value.map(p => ({ x: p.time, y: p[inf.descricao] })),
      borderColor: cores[idx % cores.length],
      backgroundColor: cores[idx % cores.length],
      tension: 0.4,
      fill: false,
      pointRadius: muitosPontos ? 0 : 3,
      pointHoverRadius: 6,
      pointHoverBorderWidth: 2,
      pointHoverBackgroundColor: cores[idx % cores.length],
      hidden: hiddenPorLabel[inf.descricao] ?? false
    }))

    chartInstance.update()
  }

  onMounted(() => {
    criarGrafico()
    intervaloRefresh = setInterval(atualizarGrafico, 3000)
    eventBus.on('mudou-tema', () => { criarGrafico(); atualizarGrafico() })
  })

  onBeforeUnmount(() => {
    if (intervaloRefresh) clearInterval(intervaloRefresh)
    if (chartInstance) chartInstance.destroy()
  })
</script>

<style scoped></style>